/**
 * La ficha de una opción de respuesta: forma, letra y color, siempre los tres.
 *
 * El principio IV lo exige y no es cosmético: hay daltonismo en cada aula, y la interfaz debe ser
 * jugable en escala de grises. La forma la lleva un SVG, la letra va encima y el color es el
 * tercer canal, nunca el único.
 *
 * El color de la letra (`onColor`) está medido, no elegido a ojo: con blanco, las fichas B y C
 * daban 3,87:1 y 3,42:1, por debajo del 4,5:1 que exige AA. **La comprobación automática no puede
 * verlo**, porque el fondo es un relleno de SVG y la herramienta remonta el árbol hasta el fondo
 * de la página. Lo fija `packages/shared/src/contrast.test.ts`.
 *
 * Y por lo mismo tampoco ve el límite de la forma contra el papel, que es donde la ficha D se
 * quedaba en 2,12:1. De ahí el filete de tinta de abajo.
 */

import { ANSWER_TOKENS, type AnswerShape } from '@clica/shared';

const PATHS: Record<AnswerShape, string> = {
  diamond: 'M20 2 L38 20 L20 38 L2 20 Z',
  triangle: 'M20 3 L38 36 L2 36 Z',
  square: 'M4 4 H36 V36 H4 Z',
  circle: 'M20 3 A17 17 0 1 1 19.9 3 Z',
};

/**
 * El centro **óptico** de cada forma, y cuánto sitio deja para la letra.
 *
 * El centro de la caja no sirve: el de un triángulo está a un tercio de la base, así que con la
 * letra en el medio geométrico se quedaba flotando arriba, justo donde el triángulo se estrecha,
 * y se salía visualmente de la figura. Es el caso donde más se nota, pero el rombo tiene el mismo
 * problema en menor medida: hacia las puntas apenas hay ancho.
 *
 * `centroY` va en las unidades del `viewBox` de 40. `escala` es el tamaño de letra relativo al
 * lado: una forma con menos superficie útil necesita una letra algo menor para no rozar el borde.
 */
const OPTICA: Record<AnswerShape, { readonly centroY: number; readonly escala: number }> = {
  // Centroide de un triángulo: (3 + 36 + 36) / 3 = 25.
  triangle: { centroY: 25, escala: 0.34 },
  diamond: { centroY: 20, escala: 0.38 },
  square: { centroY: 20, escala: 0.44 },
  circle: { centroY: 20, escala: 0.42 },
};

export function FichaOpcion({
  position,
  size = 40,
  sobreSuColor = false,
  className,
}: {
  position: number;
  size?: number;
  className?: string | undefined;
  /**
   * La ficha va encima de un fondo de su mismo color.
   *
   * Ocurre en el proyector, donde el botón entero lleva el relleno de la opción: ahí la forma se
   * pierde por completo y solo queda la letra, de modo que el rombo, el triángulo, el cuadrado y
   * el círculo dejan de aportar nada. Con esto la forma se recorta en el color del papel, que es
   * lo que la hace visible desde la última fila y en escala de grises.
   */
  sobreSuColor?: boolean;
}): React.JSX.Element | null {
  const token = ANSWER_TOKENS[position];
  if (token === undefined) return null;

  const relleno = sobreSuColor ? 'var(--color-ground)' : token.color;
  /*
   * Recortada sobre su propio color, la letra va en TINTA y no en el color de la opción.
   *
   * En el color de la opción, la D daba 2,12:1 sobre el papel del recorte: la letra de una de las
   * cuatro fichas era ilegible en el proyector. Y el color no se pierde por eso: lo lleva el
   * relleno del botón que hay detrás, que es de lo que la forma se está recortando.
   */
  const letra = sobreSuColor ? 'var(--color-edge)' : token.onColor;
  const { centroY, escala } = OPTICA[token.shape];

  return (
    <span
      aria-hidden="true"
      className={['inline-flex shrink-0', className ?? ''].join(' ').trim()}
      style={{ width: size, height: size }}
    >
      <svg viewBox="0 0 40 40" width={size} height={size} focusable="false">
        {/*
          **El filete de tinta no es adorno: es lo que hace legal la ficha.**

          Contra el papel, las cuatro fichas dan 4,89 · 3,64 · 3,22 y 2,12 a uno. La D no llega
          al 3:1 que WCAG 1.4.11 exige a un límite de componente, y no hay forma de arreglarlo
          tocando el color sin salirse de Okabe-Ito, que es lo que hace la pantalla legible con
          daltonismo. Con el filete, el límite lo pone la tinta y es 17,04:1 para las cuatro.

          El grosor va en unidades del viewBox: a 30 px de lado se pintan 1,9; a 112, siete. El
          trazo está centrado en el camino, y las cuatro formas dejan al menos 1,25 unidades de
          margen dentro de la caja, así que ninguna se recorta.
        */}
        <path
          d={PATHS[token.shape]}
          fill={relleno}
          stroke="var(--color-edge)"
          strokeWidth={2.5}
          strokeLinejoin="round"
        />
        {/*
          La letra va DENTRO del SVG y no encima como elemento aparte.

          Así se centra en las mismas unidades en que está dibujada la forma, y no en las de la
          caja: `dominant-baseline` la asienta sobre su propio centro vertical, de modo que la
          altura de la mayúscula queda repartida a los dos lados del centro óptico. Puesta fuera,
          había que compensar a ojo el descuelgue de la tipografía y el resultado bailaba con cada
          tamaño.
        */}
        <text
          x={20}
          y={centroY}
          fill={letra}
          fontSize={40 * escala}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="central"
          // La tipografía de titulares: es la que da a las letras su ancho parejo.
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {token.letter}
        </text>
      </svg>
    </span>
  );
}

/** La letra de una opción, para los textos: «la opción B está vacía». */
export function letterFor(position: number): string {
  return ANSWER_TOKENS[position]?.letter ?? String(position + 1);
}
